import React from 'react'
import styled from 'styled-components'
import Card from '@jarvis.oddle/core/components/Card'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form from '@jarvis.oddle/core/components/Form'
import Label, { labelStates } from '@jarvis.oddle/core/components/Label'
import IconDelete from '@jarvis.oddle/core/components/Icons/IconDelete'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import { H4 as H3, P } from '@jarvis.oddle/core/components/Typo'
import {
  borderRadiusSizes,
  iconSizes,
  marginSizes,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { getLocalizedAddressCard } from '@jarvis.oddle/core/tools/address'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

const DefaultContainer = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  paddingLeft: paddingSizes.medium,
  paddingRight: paddingSizes.medium,
  paddingTop: paddingSizes.small,
  paddingBottom: paddingSizes.small
}))`
  display: flex;
  flex-direction: row;
  align-items: center;
  &:hover,
  &:focus {
    cursor: pointer;
    pointer-events: auto;
  }
`

const ButtonsContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center'
}))``

interface AddressBookCardProps {
  address: {
    id: string
    customerId: string
    autoCompleteAddress: string
    address: string
    addressTwo: string
    city: string
    state: string
    postal: string
    country: string
    latitude: number
    longitude: number
    newAddress: boolean
    formattedAddress: string
    buildingName: string
    addressType: string
    unitNumber: string
    preferredDeliveryAddress: boolean
  }
  className?: string
  defaultAddress?: boolean
  onClickRadio?: (x) => void
  onClickDelete?: (x) => void
  withActionIcons?: boolean
  Container?: any
  countryCode: string
  error?: string
}

const AddressBookCard = ({
  address,
  className,
  defaultAddress = false,
  onClickRadio,
  onClickDelete,
  withActionIcons = true,
  Container = DefaultContainer,
  countryCode,
  error
}: AddressBookCardProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const localizedAddressCard = getLocalizedAddressCard({ countryCode, address })

  return (
    <Form.Radio
      className={className}
      value={address.id}
      data-testid='addressBookCard'
      onClick={onClickRadio}
    >
      {({ checked, disabled }) => (
        <Flex flexDirection='column' width='100%'>
          <Container error={error && checked}>
            <Form.Radio.Dot checked={checked} disabled={disabled} />
            <Flex
              flexDirection='column'
              justifyContent='center'
              marginLeft={marginSizes.medium}
              marginRight={marginSizes.medium}
              alignItems='flex-start'
              flexGrow={1}
            >
              {defaultAddress && (
                <Div marginBottom={marginSizes.small}>
                  <Label state={labelStates.draft} dynamic>
                    {t('Default')}
                  </Label>
                </Div>
              )}
              <H3
                bold
                marginTop={marginSizes.none}
                marginBottom={marginSizes.none}
              >
                {address.autoCompleteAddress ||
                  localizedAddressCard.firstLine.filter((x) => x).join(' ')}
              </H3>
              <P
                marginTop={marginSizes.extraSmall}
                marginBottom={marginSizes.extraSmall}
              >
                {address.autoCompleteAddress
                  ? address.addressTwo
                  : localizedAddressCard.secondLine.filter((x) => x).join(' ')}
              </P>
            </Flex>
            {withActionIcons && (
              <ButtonsContainer>
                <DivButton
                  data-testid='addressBookCardDelete'
                  onClick={onClickDelete}
                  margin={marginSizes.none}
                >
                  <IconDelete iconSize={iconSizes.medium} />
                </DivButton>
              </ButtonsContainer>
            )}
          </Container>
          {error && checked && <Form.FieldError>{error}</Form.FieldError>}
        </Flex>
      )}
    </Form.Radio>
  )
}

export default AddressBookCard
